/**
 * Pricing Page - With Framer Motion Animations
 * Σύγχρονη σελίδα τιμολόγησης
 */

import React, { useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';
import { buildUrl } from '../lib/seo';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Check,
  Close,
  Star,
  ArrowBack,
  ArrowForward,
  ExpandMore,
  Bolt,
  Diamond,
  Rocket,
} from '@mui/icons-material';
import { motion, useInView } from 'framer-motion';

// Motion components
const MotionBox = motion(Box);
const MotionCard = motion(Card);
const MotionButton = motion(Button);
const MotionTypography = motion(Typography);

// ===========================================
// DATA
// ===========================================

const plans = [
  {
    name: 'Free',
    price: '€0',
    priceYearly: '€0',
    period: 'για πάντα',
    description: 'Για μικρές επιχειρήσεις που ξεκινούν',
    icon: <Bolt sx={{ fontSize: 32 }} />,
    color: '#64748b',
    features: [
      { text: 'Έως 5 ενεργοί υπάλληλοι', included: true },
      { text: 'Βάρδιες & Πληρωμές', included: true },
      { text: 'Έσοδα & Έξοδα', included: true },
      { text: 'Dashboard', included: true },
      { text: 'Email invitations & ρόλοι', included: true },
      { text: 'myDATA Sync', included: false },
      { text: 'Export PDF/Excel', included: false },
      { text: 'AI Assistant', included: false },
      { text: 'Priority Support', included: false },
    ],
    buttonText: 'Ξεκίνα Δωρεάν',
    popular: false,
  },
  {
    name: 'Pro',
    price: '€9,90',
    priceYearly: '€7,92',
    period: '/μήνα',
    description: 'Για επιχειρήσεις με έως 10 ενεργούς υπαλλήλους',
    icon: <Diamond sx={{ fontSize: 32 }} />,
    color: '#22c55e',
    features: [
      { text: 'Έως 10 ενεργοί υπάλληλοι', included: true },
      { text: 'Όλα τα features', included: true },
      { text: 'myDATA Sync', included: true },
      { text: 'Export PDF/Excel', included: true },
      { text: 'AI Assistant', included: true },
      { text: 'Priority Support', included: true },
    ],
    buttonText: 'Δοκίμασε 14 Ημέρες Δωρεάν',
    popular: true,
    trial: '14 ημέρες δωρεάν δοκιμή',
  },
  {
    name: 'Business',
    price: '€19',
    priceYearly: '€15,20',
    period: '/μήνα',
    description: 'Για εστιατόρια με μεγαλύτερο προσωπικό',
    icon: <Rocket sx={{ fontSize: 32 }} />,
    color: '#008060',
    features: [
      { text: 'Απεριόριστοι ενεργοί υπάλληλοι', included: true },
      { text: 'Όλα τα features', included: true },
      { text: 'myDATA Sync', included: true },
      { text: 'Export PDF/Excel', included: true },
      { text: 'AI Assistant', included: true },
      { text: 'Priority Support', included: true },
    ],
    buttonText: 'Επίλεξε Business',
    popular: false,
    trial: '14 ημέρες δωρεάν δοκιμή',
  },
];

const faqs = [
  {
    question: 'Πώς λειτουργεί η δωρεάν δοκιμή;',
    answer: 'Όταν ξεπεράσεις τους 5 ενεργούς υπαλλήλους του Free plan, μπορείς να δοκιμάσεις Pro ή Business για 14 ημέρες δωρεάν. Μετά τη λήξη, επιστρέφεις αυτόματα στο Free.',
  },
  {
    question: 'Μπορώ να αλλάξω plan αργότερα;',
    answer: 'Φυσικά! Μπορείς να αναβαθμίσεις ή να υποβαθμίσεις οποιαδήποτε στιγμή. Η αλλαγή είναι άμεση.',
  },
  {
    question: 'Τι συμβαίνει με τα δεδομένα μου αν υποβαθμίσω;',
    answer: 'Τα δεδομένα σου παραμένουν ασφαλή. Αν έχεις περισσότερους ενεργούς υπαλλήλους από το όριο του Free, θα χρειαστεί να μειώσεις ενεργούς ή να αναβαθμίσεις.',
  },
  {
    question: 'Ποιους τρόπους πληρωμής δέχεστε;',
    answer: 'Δεχόμαστε όλες τις κάρτες (Visa, Mastercard, American Express) μέσω Stripe. Επίσης, δεχόμαστε τραπεζικές μεταφορές για ετήσια πληρωμή.',
  },
  {
    question: 'Υπάρχει έκπτωση για ετήσια πληρωμή;',
    answer: 'Ναι! Με ετήσια πληρωμή εξοικονομείς 20%. Το Pro κοστίζει €7,92/μήνα και το Business €15,20/μήνα.',
  },
  {
    question: 'Μπορώ να ακυρώσω οποτεδήποτε;',
    answer: 'Απολύτως! Δεν υπάρχει δέσμευση. Μπορείς να ακυρώσεις τη συνδρομή σου οποιαδήποτε στιγμή από τις ρυθμίσεις.',
  },
];

// ===========================================
// ANIMATION VARIANTS
// ===========================================

const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
  }
};

// ===========================================
// COMPONENTS
// ===========================================

function AnimatedSection({ children, variants = fadeInUp, ...props }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <MotionBox
      ref={ref}
      variants={variants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      {...props}
    >
      {children}
    </MotionBox>
  );
}

// Pricing Card Component
function PricingCard({ plan, isYearly, index }) {
  const price = isYearly ? plan.priceYearly : plan.price;
  
  return (
    <MotionCard
      variants={fadeInUp}
      whileHover={{ 
        y: -16, 
        boxShadow: `0 30px 60px -15px ${plan.color}40`,
        transition: { duration: 0.3 }
      }}
      sx={{
        height: '100%',
        position: 'relative',
        background: plan.popular 
          ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(34, 197, 94, 0.05))'
          : 'rgba(255,255,255,0.03)',
        backdropFilter: 'blur(20px)',
        border: plan.popular 
          ? '2px solid rgba(34, 197, 94, 0.5)' 
          : '1px solid rgba(255,255,255,0.1)',
        borderRadius: 4,
        overflow: 'visible',
      }}
    >
      {/* Popular badge */}
      {plan.popular && (
        <MotionBox
          initial={{ scale: 0, rotate: -10 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: 0.5, type: "spring" }}
          sx={{
            position: 'absolute',
            top: -16,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
          }}
        >
          <Chip
            icon={<Star sx={{ color: '#fbbf24 !important' }} />}
            label="ΔΗΜΟΦΙΛΕΣ"
            sx={{
              background: 'linear-gradient(135deg, #22c55e, #10b981)',
              color: 'white',
              fontWeight: 700,
              px: 2,
              py: 2.5,
              fontSize: '0.85rem',
              boxShadow: '0 10px 30px rgba(34, 197, 94, 0.4)',
            }}
          />
        </MotionBox>
      )}

      <CardContent sx={{ p: 4, pt: plan.popular ? 5 : 4 }}>
        {/* Icon */}
        <MotionBox
          whileHover={{ rotate: [0, -10, 10, 0], transition: { duration: 0.5 } }}
          sx={{
            width: 64,
            height: 64,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${plan.color}30, ${plan.color}10)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: plan.color,
            mb: 3,
          }}
        >
          {plan.icon}
        </MotionBox>

        {/* Plan name */}
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#f8fafc', mb: 1 }}>
          {plan.name}
        </Typography>
        
        <Typography sx={{ color: '#64748b', mb: 3, minHeight: 48 }}>
          {plan.description}
        </Typography>

        {/* Price */}
        <Box sx={{ mb: 3 }}>
          {plan.originalPrice && (
            <Typography
              component="span"
              sx={{
                textDecoration: 'line-through',
                color: '#64748b',
                fontSize: '1.2rem',
                mr: 1,
              }}
            >
              {plan.originalPrice}
            </Typography>
          )}
          <Typography
            component="span"
            sx={{
              fontSize: price === 'Custom' ? '2rem' : '3rem',
              fontWeight: 800,
              background: `linear-gradient(135deg, ${plan.color}, ${plan.color}80)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
            }}
          >
            {price}
          </Typography>
          <Typography component="span" sx={{ color: '#64748b', fontSize: '1.1rem' }}>
            {plan.period}
          </Typography>
        </Box>

        {/* Savings & Trial badges */}
        <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
          {plan.savings && (
            <Chip
              label={`Εξοικονόμηση ${plan.savings}`}
              size="small"
              sx={{ 
                bgcolor: 'rgba(34, 197, 94, 0.1)', 
                color: '#22c55e', 
                fontWeight: 600,
                border: '1px solid rgba(34, 197, 94, 0.3)',
              }}
            />
          )}
          {plan.trial && (
            <Chip
              label={plan.trial}
              size="small"
              sx={{ 
                bgcolor: 'rgba(99, 102, 241, 0.1)', 
                color: '#6366f1', 
                fontWeight: 600,
                border: '1px solid rgba(99, 102, 241, 0.3)',
              }}
            />
          )}
          {isYearly && plan.priceYearly !== 'Custom' && plan.priceYearly !== '€0' && (
            <Chip
              label="-20%"
              size="small"
              sx={{ 
                bgcolor: 'rgba(236, 72, 153, 0.1)', 
                color: '#ec4899', 
                fontWeight: 700,
                border: '1px solid rgba(236, 72, 153, 0.3)',
              }}
            />
          )}
        </Stack>

        {/* CTA Button */}
        <MotionButton
          component={RouterLink}
          to="/sign-up"
          variant={plan.popular ? "contained" : "outlined"}
          fullWidth
          size="large"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          sx={{
            py: 1.5,
            mb: 3,
            fontWeight: 700,
            borderRadius: 2,
            ...(plan.popular && {
              background: 'linear-gradient(135deg, #22c55e, #10b981)',
              boxShadow: '0 10px 30px rgba(34, 197, 94, 0.3)',
              '&:hover': { 
                background: 'linear-gradient(135deg, #16a34a, #059669)',
                boxShadow: '0 15px 40px rgba(34, 197, 94, 0.4)',
              },
            }),
            ...(!plan.popular && {
              borderColor: `${plan.color}50`,
              color: plan.color,
              '&:hover': { 
                borderColor: plan.color,
                bgcolor: `${plan.color}10`,
              },
            }),
          }}
        >
          {plan.buttonText}
        </MotionButton>

        {/* Divider */}
        <Box sx={{ height: 1, bgcolor: 'rgba(255,255,255,0.1)', mb: 3 }} />

        {/* Features list */}
        <List disablePadding>
          {plan.features.map((feature, fIndex) => (
            <ListItem key={fIndex} disableGutters sx={{ py: 0.75 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                {feature.included ? (
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      bgcolor: 'rgba(34, 197, 94, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Check sx={{ color: '#22c55e', fontSize: 16 }} />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      bgcolor: 'rgba(100, 116, 139, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Close sx={{ color: '#475569', fontSize: 14 }} />
                  </Box>
                )}
              </ListItemIcon>
              <ListItemText
                primary={feature.text}
                primaryTypographyProps={{
                  sx: { 
                    color: feature.included ? '#f8fafc' : '#475569',
                    fontSize: '0.95rem',
                  },
                }}
              />
            </ListItem>
          ))}
        </List>
      </CardContent>
    </MotionCard>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function PricingPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [expandedFaq, setExpandedFaq] = useState(false);
  const isYearly = billingPeriod === 'yearly';

  const pageUrl = buildUrl('/pricing');
  const ogImage = buildUrl('/og-image.png');

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: '#0f172a',
      overflowX: 'hidden',
      position: 'relative',
    }}>
      <Helmet>
        <title>Τιμοκατάλογος | Majestify – Free, Pro &amp; Enterprise Plans</title>
        <meta name="description" content="Free έως 5 ενεργούς υπαλλήλους. Pro €9,90/μήνα (10 ενεργοί). Business €19/μήνα (απεριόριστοι). Δωρεάν δοκιμή 14 ημερών." />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content="Τιμοκατάλογος | Majestify" />
        <meta property="og:description" content="Free έως 5 ενεργούς. Pro €9,90/μήνα. Business €19/μήνα. Δωρεάν δοκιμή 14 ημερών." />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="el_GR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Τιμοκατάλογος | Majestify" />
        <meta name="twitter:description" content="Free έως 5 ενεργούς. Pro €9,90/μήνα. Business €19/μήνα. Δωρεάν δοκιμή 14 ημερών." />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>
      {/* Gradient overlays */}
      <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'radial-gradient(ellipse at 20% 20%, rgba(34, 197, 94, 0.1) 0%, transparent 50%)', pointerEvents: 'none', zIndex: 1 }} />
      <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'radial-gradient(ellipse at 80% 80%, rgba(99, 102, 241, 0.1) 0%, transparent 50%)', pointerEvents: 'none', zIndex: 1 }} />

      {/* Content */}
      <Box sx={{ position: 'relative', zIndex: 2 }}>
        {/* Header */}
        <MotionBox
          component="header"
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.6 }}
          sx={{
            py: 2,
            px: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backdropFilter: 'blur(20px)',
            bgcolor: 'rgba(15, 23, 42, 0.8)',
            position: 'sticky',
            top: 0,
            zIndex: 100,
            borderBottom: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <MotionButton
            component={RouterLink}
            to="/"
            startIcon={<ArrowBack />}
            whileHover={{ x: -5 }}
            sx={{ color: '#94a3b8' }}
          >
            Αρχική
          </MotionButton>
          <MotionTypography
            variant="h5"
            whileHover={{ scale: 1.05 }}
            sx={{
              fontWeight: 800,
              background: 'linear-gradient(135deg, #22c55e, #10b981)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              cursor: 'pointer',
            }}
          >
            🌴 Palmera
          </MotionTypography>
          <MotionButton
            component={RouterLink}
            to="/sign-in"
            variant="outlined"
            whileHover={{ scale: 1.05 }}
            sx={{ 
              borderColor: 'rgba(34, 197, 94, 0.5)', 
              color: '#22c55e',
              borderRadius: 2,
              display: { xs: 'none', sm: 'flex' },
            }}
          >
            Σύνδεση
          </MotionButton>
        </MotionBox>

        {/* Hero */}
        <Box sx={{ py: { xs: 8, md: 12 }, textAlign: 'center' }}>
          <Container maxWidth="md">
            <MotionBox
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Chip
                label="💰 Εξοικονόμηση 20% με ετήσια πληρωμή"
                sx={{
                  background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(16, 185, 129, 0.1))',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  color: '#22c55e',
                  fontWeight: 600,
                  mb: 4,
                  py: 2.5,
                }}
              />
              
              <Typography
                variant={isMobile ? 'h3' : 'h2'}
                sx={{ fontWeight: 800, mb: 2, color: '#f8fafc' }}
              >
                Απλή & Διαφανής{' '}
                <Box 
                  component="span" 
                  sx={{ 
                    background: 'linear-gradient(135deg, #22c55e, #10b981)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    color: 'transparent',
                  }}
                >
                  Τιμολόγηση
                </Box>
              </Typography>
              
              <Typography variant="h6" sx={{ color: '#64748b', mb: 5, fontWeight: 400 }}>
                Επίλεξε το plan που ταιριάζει στην επιχείρησή σου
              </Typography>

              {/* Billing Toggle */}
              <MotionBox
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
                sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}
              >
                <ToggleButtonGroup
                  value={billingPeriod}
                  exclusive
                  onChange={(e, value) => value && setBillingPeriod(value)}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.05)',
                    borderRadius: 3,
                    p: 0.5,
                    '& .MuiToggleButton-root': {
                      border: 'none',
                      borderRadius: 2,
                      px: 4,
                      py: 1.5,
                      color: '#64748b',
                      fontWeight: 600,
                      '&.Mui-selected': {
                        bgcolor: 'rgba(34, 197, 94, 0.2)',
                        color: '#22c55e',
                        '&:hover': {
                          bgcolor: 'rgba(34, 197, 94, 0.3)',
                        },
                      },
                    },
                  }}
                >
                  <ToggleButton value="monthly">Μηνιαία</ToggleButton>
                  <ToggleButton value="yearly">
                    Ετήσια
                    <Chip 
                      label="-20%" 
                      size="small" 
                      sx={{ 
                        ml: 1, 
                        bgcolor: '#ec4899', 
                        color: 'white', 
                        fontWeight: 700,
                        height: 20,
                        fontSize: '0.7rem',
                      }} 
                    />
                  </ToggleButton>
                </ToggleButtonGroup>
              </MotionBox>
            </MotionBox>
          </Container>
        </Box>

        {/* Plans */}
        <Container maxWidth="lg" sx={{ pb: 15 }}>
          <MotionBox
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <Grid container spacing={4} justifyContent="center">
              {plans.map((plan, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <PricingCard plan={plan} isYearly={isYearly} index={index} />
                </Grid>
              ))}
            </Grid>
          </MotionBox>
        </Container>

        {/* Money-back guarantee */}
        <Box sx={{ py: 10, bgcolor: 'rgba(34, 197, 94, 0.05)' }}>
          <Container maxWidth="md">
            <AnimatedSection sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: 'rgba(34, 197, 94, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                  fontSize: '2.5rem',
                }}
              >
                🛡️
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#f8fafc', mb: 2 }}>
                Εγγύηση Επιστροφής Χρημάτων
              </Typography>
              <Typography sx={{ color: '#64748b', fontSize: '1.1rem', maxWidth: 600, mx: 'auto' }}>
                Δεν είσαι ικανοποιημένος; Επιστρέφουμε τα χρήματά σου εντός 30 ημερών, 
                χωρίς ερωτήσεις. Πιστεύουμε τόσο πολύ στο προϊόν μας!
              </Typography>
            </AnimatedSection>
          </Container>
        </Box>

        {/* FAQs */}
        <Box sx={{ py: 15 }}>
          <Container maxWidth="md">
            <AnimatedSection sx={{ textAlign: 'center', mb: 8 }}>
              <Typography variant="overline" sx={{ color: '#22c55e', fontWeight: 600, letterSpacing: 3, mb: 2, display: 'block' }}>
                FAQ
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, color: '#f8fafc' }}>
                Συχνές Ερωτήσεις
              </Typography>
            </AnimatedSection>
            
            <AnimatedSection>
              {faqs.map((faq, index) => (
                <Accordion
                  key={index}
                  expanded={expandedFaq === index}
                  onChange={() => setExpandedFaq(expandedFaq === index ? false : index)}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '16px !important',
                    mb: 2,
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': { margin: '0 0 16px 0' },
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore sx={{ color: '#22c55e' }} />}
                    sx={{ py: 1 }}
                  >
                    <Typography sx={{ fontWeight: 600, color: '#f8fafc' }}>{faq.question}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography sx={{ color: '#94a3b8', lineHeight: 1.8 }}>{faq.answer}</Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </AnimatedSection>
          </Container>
        </Box>

        {/* CTA Section */}
        <Box sx={{ py: 15, position: 'relative' }}>
          <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent, rgba(34, 197, 94, 0.1), transparent)' }} />
          <Container maxWidth="md" sx={{ position: 'relative' }}>
            <AnimatedSection variants={scaleIn}>
              <Box
                sx={{
                  background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(34, 197, 94, 0.05))',
                  backdropFilter: 'blur(20px)',
                  borderRadius: 6,
                  p: { xs: 4, md: 8 },
                  textAlign: 'center',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, color: '#f8fafc' }}>
                  Έτοιμος να ξεκινήσεις;
                </Typography>
                <Typography sx={{ color: '#94a3b8', mb: 5, fontSize: '1.1rem' }}>
                  Δοκίμασε το Pro plan για 30 ημέρες δωρεάν. Χωρίς δεσμεύσεις, χωρίς κάρτα.
                </Typography>
                <MotionButton
                  component={RouterLink}
                  to="/sign-up"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  whileHover={{ scale: 1.05, boxShadow: '0 25px 50px rgba(34, 197, 94, 0.4)' }}
                  whileTap={{ scale: 0.95 }}
                  sx={{
                    background: 'linear-gradient(135deg, #22c55e, #10b981)',
                    px: 8,
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    borderRadius: 3,
                  }}
                >
                  Ξεκίνα Δωρεάν Τώρα
                </MotionButton>
              </Box>
            </AnimatedSection>
          </Container>
        </Box>

        {/* Footer */}
        <Box
          component="footer"
          sx={{
            py: 4,
            textAlign: 'center',
            borderTop: '1px solid rgba(255,255,255,0.05)',
          }}
        >
          <Typography variant="body2" sx={{ color: '#475569' }}>
            © {new Date().getFullYear()} Palmera. All rights reserved.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
